import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Message {
  role: 'user' | 'bot';
  content: string;
}

const SYSTEM_PROMPT = "You are Khodo, a friendly and highly knowledgeable financial assistant for KwachaLite. Your expertise is in personal finance, specifically savings strategies and budgeting. Keep your answers concise, encouraging, and human-like. Use Malawian Kwacha (K) when referring to money. You help users understand how to save better and manage their budgets effectively using the KwachaLite app.";

export function KhodoAI() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'bot', content: "Hi! I'm Khodo, your financial guide. I can help you with savings strategies or budgeting tips. What's on your mind?" }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    const userMsg = input.trim();
    const newUserMsg: Message = { role: 'user', content: userMsg };
    const updatedHistory: Message[] = [...messages, newUserMsg];
    setMessages(updatedHistory);
    setInput('');
    setIsTyping(true);

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_ANTHROPIC_API_KEY || '',
          'anthropic-version': '2023-06-01',
          'dangerously-allow-browser': 'true' // Note: Direct browser calls are not recommended for production
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20240620',
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: updatedHistory.map(m => ({
            role: m.role === 'bot' ? 'assistant' : 'user',
            content: m.content
          }))
        })
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      const botText = data.content?.[0]?.text ?? "Couldn't connect. Try again.";
      setMessages(prev => [...prev, { role: 'bot', content: botText } as Message]);
    } catch (error) {
      console.error('Khodo AI Error:', error);
      setMessages(prev => [...prev, { role: 'bot', content: "I'm having trouble connecting right now. Please try again in a moment." } as Message]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100] font-sans">
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'} transition-all duration-500 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl shadow-blue-500/40 flex items-center justify-center group`}
      >
        <div className="absolute -top-2 -left-2 bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-500/20 shadow-sm animate-bounce">
          Talk to Khodo
        </div>
        <Sparkles className="w-6 h-6 group-hover:rotate-12 transition-transform" />
      </button>

      {/* Chat Window */}
      <div className={`${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-10 pointer-events-none'} transition-all duration-500 absolute bottom-0 right-0 w-[350px] sm:w-[400px] h-[500px] bg-white dark:bg-[#0F1115] border border-slate-200 dark:border-white/10 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.3)] flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="p-6 bg-blue-600 dark:bg-blue-700 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold leading-none">Khodo AI</h3>
              <p className="text-[10px] text-blue-100 uppercase tracking-widest font-bold mt-1">Financial Assistant</p>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-transparent">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-4 rounded-2xl text-sm ${
                msg.role === 'user' 
                  ? 'bg-blue-600 text-white rounded-tr-none shadow-lg shadow-blue-500/10' 
                  : 'bg-white dark:bg-white/5 text-slate-800 dark:text-white rounded-tl-none border border-slate-200 dark:border-white/5'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white dark:bg-white/5 p-4 rounded-2xl rounded-tl-none border border-slate-200 dark:border-white/5">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" />
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:0.2s]" />
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:0.4s]" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 bg-white dark:bg-[#0F1115] border-t border-slate-200 dark:border-white/10">
          <div className="flex items-center gap-2 bg-slate-100 dark:bg-white/5 rounded-xl p-1 px-3 border border-slate-200 dark:border-white/10">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask about savings or budgets..."
              className="flex-1 bg-transparent border-none py-2 text-sm focus:ring-0 dark:text-white"
            />
            <button 
              onClick={handleSend}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="flex justify-center gap-4 mt-3">
             <button onClick={() => setInput('How can I save more?')} className="text-[10px] font-bold text-slate-400 hover:text-blue-500 uppercase tracking-wider transition-colors">Savings Tips</button>
             <button onClick={() => setInput('How to set a budget?')} className="text-[10px] font-bold text-slate-400 hover:text-blue-500 uppercase tracking-wider transition-colors">Budget Help</button>
          </div>
        </div>
      </div>
    </div>
  );
}